import {
    ContentType,
    IPictureFileMetadataResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { FileType } from 'utils/file';

/**
 * Error when the file type that is uploaded is not supported.
 */
export class UnsupportedFileTypeError extends Error {}

/**
 * Error when the uploaded file is too large in size.
 */
export class FileTooLargeError extends Error {}

/**
 * Error when the uploaded file name is too long.
 */
export class FileNameTooLongError extends Error {}

/**
 * The default maximum file size that can be uploaded.
 */
export const defaultMaxFileSize = 10 * 1024 * 1024; // 10MB

/**
 * The default maximum file name length.
 */
export const defaultMaxFileNameLength = 212;

/**
 * List of valid MIME types for pictures, e.g. `image/jpeg`.
 *
 * See: https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
 */
export const pictureMimeTypes: string[] = [FileType.Jpg, FileType.Png, FileType.Webp];

export const isValidPicture = (file: File): boolean =>
    pictureMimeTypes.includes(file.type.toLowerCase());

/**
 * Payload object for a file upload.
 */
export type FileUpload = {
    /**
     * The name of the file, e.g. `my-file.jpg`.
     */
    name: string;
    /**
     * The actual file data (blob).
     *
     * See: https://developer.mozilla.org/en-US/docs/Web/API/File
     */
    data: File;
};

export type DeleteFileText = Readonly<{
    actionText: string;
    checkText: string;
}>;

export const getDeleteFileModalText = (
    rowData: Pick<IPictureFileMetadataResponse, 'FileType' | 'FolderCount' | 'FileCount'>
): DeleteFileText => {
    if (rowData.FileType === ContentType.Folder) {
        return {
            actionText: `delete this folder? It contains ${
                rowData.FolderCount ?? 0
            } folders and ${
                rowData.FileCount ?? 0
            } files. The data will no longer be available.`,
            checkText: 'delete this folder and all its contents',
        };
    }

    return {
        actionText: 'delete this file? The data will no longer be available.',
        checkText: 'delete this file and its data',
    };
};

/**
 * Regex for excluding special characters from folder name.
 */
export const excludeSpecialChars = /^[^:<>"/\\|?*]*$/;

export const fileValidation = (file: File) => {
    if (!isValidPicture(file)) {
        return 'Unsupported file type. Please upload a .PNG, .JPG, or .WEBP file.';
    }
    if (file.size > defaultMaxFileSize) {
        return 'File is too large. Please upload a file smaller than 10MB.';
    }
    if (file.name.length > defaultMaxFileNameLength) {
        return 'File name is too long.';
    }
    return undefined;
};
